import styles from './podcasts-id.module.scss'
import cn from 'classnames'
import Badge from '@/components/Badge'
import Breadcrumbs from '@/components/Breadcrumbs'
import DynamicContent from '@/components/DynamicContent'
import PageHeader from '@/components/PageHeader/PageHeader'
import useFormatDate from '@/hooks/useFormatDate'
import AppLayout from '@/layouts/AppLayout'
import ContentLayout from '@/layouts/ContentLayout'
import LayoutService from '@/services/LayoutService'
import PodcastService from '@/services/PodcastService'
import getSectionData from '@/utils/getSectionData'
import resolveSidebarBlocks from '@/utils/resolveSidebarBlocks'

function Page({ podcast, layoutData }) {
  const breadcrumbs = [{ title: 'Podcasts', uri: '/podcasts' }, { title: podcast.title }]
  const date = useFormatDate({ date: podcast.date, day: 'numeric', month: 'long', year: 'numeric' })
  const acfMain = podcast.dynamicAcf.dynamicContent
  const acfAside = resolveSidebarBlocks(podcast)

  return (
    <AppLayout
      page={podcast}
      data={layoutData}
    >
      <PageHeader data={podcast.sections} />

      <ContentLayout className={cn(styles['root'])}>
        <ContentLayout.Main
          previousUrl={podcast?.previous?.uri}
          nextUrl={podcast?.next?.uri}
        >
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            className="pb-12"
          />

          <ContentLayout.Main.IntroHeader>
            <ContentLayout.Main.IntroHeader.Content className={cn(styles['introheader__content'])}>
              <time>{date}</time>

              <h1>{podcast.title}</h1>

              {podcast.podcastAcf.shortDescription && <p>{podcast.podcastAcf.shortDescription}</p>}

              {podcast.categories?.nodes.length > 0 && (
                <ul>
                  {podcast.categories?.nodes.map((category) => {
                    return (
                      <li key={category.name}>
                        <Badge searchable>{category.name}</Badge>
                      </li>
                    )
                  })}
                </ul>
              )}
            </ContentLayout.Main.IntroHeader.Content>

            <ContentLayout.Main.IntroHeader.Media className={cn(styles['introheader__media'])}>
              {podcast.podcastAcf.youtubeId && (
                <iframe
                  title="YouTube video"
                  src={`https://www.youtube.com/embed/${podcast.podcastAcf.youtubeId}`}
                />
              )}
            </ContentLayout.Main.IntroHeader.Media>
          </ContentLayout.Main.IntroHeader>

          <DynamicContent data={acfMain} />
        </ContentLayout.Main>

        <ContentLayout.Aside className={cn(styles['root__aside'])}>
          <DynamicContent data={acfAside} />
        </ContentLayout.Aside>
      </ContentLayout>
    </AppLayout>
  )
}

export async function getStaticPaths() {
  let podcasts = []
  const BASE_URL = `${process.env.NEXT_PUBLIC_CMS_REST_ENDPOINT}/wp/v2/podcast?per_page=100&_fields=slug`
  const { headers } = await fetch(BASE_URL)
  const pages = headers.get('x-wp-totalpages')

  for (let page = 1; page <= pages; page++) {
    const response = await fetch(`${BASE_URL}&page=${page}`)
    podcasts = [...podcasts, ...(await response.json())]
  }

  return {
    paths: podcasts.map((podcast) => ({ params: { id: podcast.slug } })),
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, preview, previewData }) {
  const { id } = params
  const isId = Number.isInteger(Number(id))

  try {
    const { data } = await PodcastService().show(id, isId ? 'DATABASE_ID' : 'URI', previewData)
    const { data: layoutData } = await LayoutService().index()
    let podcast = data.podcast

    if (!podcast) return { notFound: true }

    if (preview) {
      podcast = { ...podcast, ...data.podcast.revisions.nodes[0] }
    }

    podcast.sections = getSectionData(data.page.sections)

    return {
      props: { podcast, layoutData },
      revalidate: 10,
    }
  } catch (error) {
    throw new Error(error)
  }
}

export default Page
